/* eslint-disable camelcase, no-undef */

import { get, set } from 'lodash'

// Persist only what the API layer needs to keep the session alive across a full page
// reload (F5, Stripe redirect). Anything not named here is intentionally NOT persisted.
// Never add a secret or PII (pentest 6.1 / 6.3 / 6.7).
//
// Profiles, energy scores, account data, My Network, SenSai credentials, etc. are never
// written to browser storage: after a reload, SessionRestore calls GET /sessions/current
// and rebuilds them in memory (SIM-388).
export const PERSISTED_PATHS = Object.freeze([
  // Lets the route guards treat the user as logged in while the session is restored.
  'session.isAuthenticated',
  // Tells ApiEndpoint that DPoP was in use, so it re-binds a fresh key pair after the
  // reload. The non-extractable CryptoKey itself (user.dpopKeyPair) is never persisted.
  'user.dpopActive',
  // Temporary: the session token still has to survive a reload, because nothing else
  // can restore the session yet. Removing this entry is the frontend half of the
  // authentication change tracked on SIM-365 — do not drop it before then or users
  // are signed out on every refresh.
  'user.token',
  // Authorization for SSO / impersonation uses these instead of user.token. Both
  // flows round-trip through a full page load and must stay logged in on refresh.
  'user.ssoToken',
  'user.impersonateToken',
  // Drives the session-expiration timer.
  'user.tokenExpiration',
])

// Copies only the PERSISTED_PATHS out of a state object. Used on write and on read, so
// state written by an older build (or by hand) never reaches the store either.
export const pickPersistedState = state =>
  PERSISTED_PATHS.reduce((acc, path) => {
    const value = get(state, path)
    return value === undefined ? acc : set(acc, path, value)
  }, {})

export const BUILD_STORAGE_KEY = 'build'
export const STATE_STORAGE_KEY = 'state'

export const reseedBuildHash = () => {
  try {
    localStorage.setItem(BUILD_STORAGE_KEY, __webpack_hash__)
  } catch (error) {
    // Privacy modes can throw on storage access.
  }
}

const clearLocalStorageSafely = () => {
  try {
    localStorage.clear()
  } catch (error) {
    // Privacy modes can throw on storage access.
  }
}

export const loadState = () => {
  try {
    const serializedState = localStorage.getItem(STATE_STORAGE_KEY)
    const build = localStorage.getItem(BUILD_STORAGE_KEY)
    const hasState = serializedState !== null

    if (build !== __webpack_hash__) {
      if (hasState) {
        clearLocalStorageSafely()
      }

      reseedBuildHash()

      return undefined
    }

    if (!hasState) {
      return undefined
    }

    // Re-apply the allowlist on read so anything written by an older build never
    // reaches the store.
    const state = pickPersistedState(JSON.parse(serializedState))

    // DPoP: the private key is non-extractable and lives only in Redux memory.
    // It is always gone after a page refresh. Clear it so request() knows there
    // is no key pair and omits the DPoP header.
    //
    // state.user.dpopActive stays true — the API layer uses it to detect that
    // DPoP was in use and transparently generates a new key pair + re-binds on
    // the next request (key rotation), so the user stays logged in after a
    // full-page navigation such as a Stripe redirect.
    if (state && state.user) {
      state.user.dpopKeyPair = null
    }

    // Guard against stale localStorage: a previous code version may have written
    // isAuthenticated = false while a valid token was still present. If a token
    // exists the user IS authenticated — correct the inconsistency so retryWhen
    // and routing guards behave correctly after a rebuild or forced restart.
    if (state && state.session && state.user) {
      const hasToken = state.user.token || state.user.ssoToken || state.user.impersonateToken
      if (hasToken && !state.session.isAuthenticated) {
        state.session.isAuthenticated = true
      }
    }

    return state
  } catch (error) {
    clearLocalStorageSafely()
    reseedBuildHash()
    return undefined
  }
}

let persistReduxState = true

export const suspendStatePersistence = () => {
  persistReduxState = false
}

export const resumeStatePersistence = () => {
  persistReduxState = true
}

export const saveState = state => {
  if (!persistReduxState) {
    return
  }

  try {
    const serializedState = JSON.stringify(pickPersistedState(state))
    localStorage.setItem(STATE_STORAGE_KEY, serializedState)
  } catch (error) {
    // TODO: handle error
  }
}
